import { Badge, Button, Icon, buttonClass, useConfirm, useToast } from '@/components';
import { cn } from '@/lib/cn';
import { ApiClientError } from '@uchat-team-management/shared';
import { Show } from 'solid-js';
import { useCloseReleasePreview, useReleasePreview } from './hooks';

/**
 * The Cloudflare Pages preview of a release-web build: the full URL to open or
 * copy, its live state, and Close for whoever may publish the release.
 * Publishing removes the preview automatically.
 */
export const PreviewCard = (props: {
  releaseId: string;
  url: string;
  canClose: boolean;
  published: boolean;
}) => {
  const preview = useReleasePreview(
    () => props.releaseId,
    () => props.published,
  );
  const close = useCloseReleasePreview(props.releaseId);
  const confirm = useConfirm();
  const toast = useToast();
  const state = () => preview.data?.data.state ?? 'unknown';
  const url = () => preview.data?.data.url ?? props.url;
  const closed = () => state() === 'closed';

  const copy = () =>
    navigator.clipboard.writeText(url()).then(
      () => toast.success('Preview link copied'),
      () => toast.error('Could not copy the link'),
    );

  const closePreview = async () => {
    const ok = await confirm({
      title: 'Close this preview?',
      body: 'Deletes the build from Cloudflare Pages. The link stops working for everyone.',
      confirmLabel: 'Close preview',
      danger: true,
    });
    if (!ok) return;
    try {
      await close.mutateAsync();
      toast.success('Preview closed');
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.error.message : 'Could not close preview');
    }
  };

  return (
    <section
      aria-label="Preview"
      class={cn(
        'mt-3 rounded-lg border p-3 text-sm transition-colors duration-200 ease-out motion-reduce:transition-none',
        closed()
          ? 'border-neutral-200 bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900'
          : 'border-brand-200 bg-brand-50/50 dark:border-brand-900 dark:bg-brand-900/20',
      )}
    >
      <div class="flex flex-wrap items-center justify-between gap-2">
        <div class="flex items-center gap-2">
          <Icon name="link" class="text-neutral-500" />
          <span class="font-medium">Preview</span>
          <Show when={state() === 'active'}>
            <Badge tone="success">Live</Badge>
          </Show>
          <Show when={closed()}>
            <Badge tone="neutral">Closed</Badge>
          </Show>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <Show when={!closed()}>
            <a href={url()} target="_blank" rel="noreferrer" class={buttonClass('secondary', 'sm')}>
              Open preview
            </a>
          </Show>
          <Button size="sm" variant="secondary" onClick={copy}>
            <Icon name="copy" />
            Copy link
          </Button>
          <Show when={props.canClose && state() === 'active'}>
            <Button size="sm" variant="danger" loading={close.isPending} onClick={closePreview}>
              Close preview
            </Button>
          </Show>
        </div>
      </div>
      <p
        class="mt-2 break-all font-mono text-xs"
        classList={{
          'text-brand-700 dark:text-brand-300': !closed(),
          'text-neutral-400 line-through': closed(),
        }}
      >
        {url()}
      </p>
      <Show when={closed()}>
        <p class="mt-1 text-xs text-neutral-500">
          This preview was closed or removed when the release was published.
        </p>
      </Show>
    </section>
  );
};
